/**
 * Preview Panel Component
 *
 * Shows live preview of current quiz configuration.
 * Displays map and basic stats (feature count, question count, etc.)
 * Part of Quiz Studio wizard (Phase 3).
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PreviewMap } from "./PreviewMap";
import { MapCredits } from "@/components/shared/MapCredits";
import { geojsonDatasets } from "@/data/geojsonDatasets";
import { Loader2 } from "lucide-react";
import type { WizardState } from "@/stores/useStudioStore";

interface PreviewPanelProps {
  featureCount?: number;
  totalFeatureCount?: number;
  questionCount?: number;
  filterCount?: number;
  dataset: WizardState["dataset"];
  filters?: WizardState["filters"];
  excludedFeatureIds?: string[];
  promptKeys?: string[];
  className?: string;
}

export function PreviewPanel({
  featureCount = 0,
  totalFeatureCount = 0,
  questionCount = 0,
  filterCount = 0,
  dataset,
  filters = [],
  excludedFeatureIds = [],
  promptKeys = [],
  className = "",
}: PreviewPanelProps) {
  const hasFilters = filterCount > 0;
  const isFiltered = hasFilters && featureCount !== totalFeatureCount;
  const hasDataset = dataset.collection !== null;
  const hasExclusions = excludedFeatureIds.length > 0;

  // Find dataset credits if using catalogue dataset
  const catalogueDataset = dataset.source?.type === "catalogue" && dataset.source?.id
    ? geojsonDatasets.find(d => d.id === dataset.source?.id)
    : null;

  return (
    <div className={className}>
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <CardTitle className="text-lg">Preview</CardTitle>
          {hasDataset && dataset.label && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {dataset.label}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4 flex-1 overflow-y-auto">
          {/* Map Preview */}
          {hasDataset && dataset.collection ? (
            <>
              <div className="rounded-lg overflow-hidden border">
                <PreviewMap
                  collection={dataset.collection}
                  filters={filters}
                  excludedFeatureIds={excludedFeatureIds}
                  promptKeys={promptKeys}
                />
              </div>
              {/* Map Credits */}
              {catalogueDataset?.credits && catalogueDataset.credits.length > 0 && (
                <MapCredits variant="inline" credits={catalogueDataset.credits} className="-mt-4" />
              )}
            </>
          ) : (
            <div className="aspect-video bg-muted rounded-lg flex items-center justify-center border-2 border-dashed border-border">
              <p className="text-sm text-muted-foreground text-center px-4">
                Select a dataset to see preview
              </p>
            </div>
          )}

          {/* Prompt Information */}
          {hasDataset && promptKeys.length > 0 && (
            <div className="p-3 rounded-lg bg-muted/50 space-y-2">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Prompts
              </div>
              <div className="flex flex-wrap gap-1.5">
                {promptKeys.map((key) => (
                  <Badge key={key} variant="secondary" className="text-xs">
                    {key}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Questions will be based on {promptKeys.length === 1 ? "this property" : "these properties"}
              </p>
            </div>
          )}

          {/* Stats */}
          {hasDataset && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Features:</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{featureCount}</span>
                  {isFiltered && (
                    <span className="text-xs text-muted-foreground">
                      / {totalFeatureCount}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Questions:</span>
                <span className="font-medium">{questionCount}</span>
              </div>
              {hasFilters && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Filters:</span>
                  <Badge variant="secondary" className="text-xs">
                    {filterCount} active
                  </Badge>
                </div>
              )}
              {hasExclusions && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Excluded:</span>
                  <Badge variant="outline" className="text-xs">
                    {excludedFeatureIds.length}
                  </Badge>
                </div>
              )}
            </div>
          )}

          {/* Filter/Exclusion notices */}
          <div className="space-y-2">
            {isFiltered && (
              <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                <p className="text-xs text-blue-900 dark:text-blue-100">
                  <strong>{totalFeatureCount - featureCount}</strong> features filtered out
                </p>
              </div>
            )}
            {hasExclusions && (
              <div className="p-2.5 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                <p className="text-xs text-red-900 dark:text-red-100">
                  <strong>{excludedFeatureIds.length}</strong> features excluded
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
